import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, timer } from 'rxjs';
import { catchError, filter, take, switchMap, retry } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../../services/logger.service';
import { ToastService } from '../../services/toast.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly toastService = inject(ToastService);

  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);

  // Max number of automatic retries when the API returns 429 (Too Many Requests).
  private readonly MAX_RATE_LIMIT_RETRIES = 3;

  constructor(private readonly router: Router) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Get token from AuthService
    const token = this.authService.getToken();

    // Log critical error if authenticated but no token
    if (this.authService.getIsAuthenticated() && !token) {
      this.logger.error('[API Interceptor] User authenticated but token missing', {
        url: req.url,
      });
    }

    // Clone the request and add headers
    const apiReq = this.addAuthHeaders(req, token);

    return next.handle(apiReq).pipe(
      // Automatically retry rate-limited requests (HTTP 429) with backoff.
      // Concurrent page loads (e.g. the dashboard fires several calls at once)
      // can briefly trip the API rate limiter; a short backoff lets them
      // recover instead of surfacing an error to the user.
      retry({
        count: this.MAX_RATE_LIMIT_RETRIES,
        delay: (error: HttpErrorResponse, retryCount: number) => {
          if (error.status !== 429) {
            // Only 429 is retried here; other errors fall through to catchError.
            return throwError(() => error);
          }

          const retryAfterHeader = error.headers?.get('Retry-After');
          const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : 0;
          // Honor Retry-After when provided, otherwise exponential backoff
          // (500ms, 1s, 2s...) capped at 5s.
          const backoffMs =
            retryAfterMs > 0 ? retryAfterMs : Math.min(500 * 2 ** (retryCount - 1), 5000);

          this.logger.warn(
            `[API Interceptor] 429 for ${req.method} ${req.url}, retrying in ${backoffMs}ms (attempt ${retryCount}/${this.MAX_RATE_LIMIT_RETRIES})`
          );

          return timer(backoffMs);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        // Only log non-auth errors
        if (error.status !== 401) {
          this.logger.error(`[API Interceptor] ${req.method} ${req.url} failed`, {
            status: error.status,
          });
        }

        // Session terminated by a transfer — must be checked first, before any
        // endpoint-specific early returns, so it is caught regardless of which
        // endpoint (including /auth/refresh) returns this error.
        // Use clearLocalSession() instead of logout() to avoid a second /auth/logout
        // request that would itself 401 and re-trigger this handler.
        if (error.status === 401 && error.error?.error === 'session_transferred') {
          this.toastService.showWarning(
            'Your session was terminated because you signed in on another device.',
            8000
          );
          this.authService.clearLocalSession();
          this.router.navigate(['/login'], { queryParams: { reason: 'session_transferred' } });
          return throwError(() => error);
        }

        // If 401 on auth endpoints (/auth/login, /auth/refresh, /auth/logout), don't retry - just fail
        if (
          error.status === 401 &&
          (req.url.includes('/auth/login') ||
            req.url.includes('/auth/refresh') ||
            req.url.includes('/auth/logout'))
        ) {
          this.logger.warn(
            '[API Interceptor] Authentication failed on auth endpoint, not retrying'
          );
          return throwError(() => this.handleError(error, req));
        }

        // If 401 error and not already on an auth endpoint (to avoid infinite loops)
        if (error.status === 401 && !req.url.includes('/auth/')) {
          return this.handle401Error(req, next);
        }

        return throwError(() => this.handleError(error, req));
      })
    );
  }

  private addAuthHeaders(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    const headers: { [key: string]: string } = {};

    // Only set Content-Type if it's not already set and body is not FormData
    if (!req.headers.has('Content-Type') && !(req.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Always set Accept header
    headers['Accept'] = 'application/json';

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return req.clone({
      setHeaders: headers,
      withCredentials: true, // Important: allows sending httpOnly cookies
    });
  }

  private handle401Error(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      // Wait for refresh to complete
      this.logger.debug('[API Interceptor] Waiting for ongoing token refresh');
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          this.logger.debug('[API Interceptor] Token refresh completed, retrying request');
          return next.handle(this.addAuthHeaders(req, token));
        })
      );
    }

    // Start token refresh
    this.logger.info('[API Interceptor] Starting token refresh process');
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshToken().pipe(
      switchMap((response) => {
        this.logger.info('[API Interceptor] Token refresh successful, retrying original request');
        this.isRefreshing = false;
        this.refreshTokenSubject.next(response.token);

        // Retry the failed request with new token
        return next.handle(this.addAuthHeaders(req, response.token));
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('[API Interceptor] Token refresh failed, logging out user', {
          status: error.status,
          message: error.message,
        });
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);

        const serverMsg: string = error?.error?.error ?? error?.error?.message ?? '';
        const isInactivity = serverMsg.toLowerCase().includes('inactivity');

        this.authService.logout().subscribe(() => {
          this.router.navigate(['/login'], {
            queryParams: isInactivity ? { reason: 'inactivity' } : { sessionExpired: 'true' },
          });
        });

        return throwError(() => error);
      })
    );
  }

  private handleError(error: HttpErrorResponse, _req: HttpRequest<unknown>): Error {
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      return new Error(`Client Error: ${error.error.message}`);
    }

    // Server-side error
    switch (error.status) {
      case 400:
        return new Error('Bad Request: ' + (error.error?.message || 'Invalid request'));
      case 401:
        return new Error('Unauthorized: Please login again');
      case 403:
        return new Error('Forbidden: You do not have permission');
      case 404:
        return new Error('Not Found: The requested resource was not found');
      case 500:
        return new Error('Internal Server Error: Please try again later');
      default:
        return new Error(
          `Server Error: ${error.status} - ${error.error?.message || error.message}`
        );
    }
  }
}
